<template>
    <div class="h-full w-full flex flex-col">
        <!-- Header with Institution Filter -->
        <div class="mb-6">
            <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <h2 class="text-2xl font-bold text-gray-800">Academic Calendar 2025-2026</h2>
                <div class="flex items-center gap-4">
                    <div class="flex items-center gap-2">
                        <label class="text-sm font-medium text-gray-700">Entity:</label>
                        <select v-model="selectedInstitution"
                            class="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent">
                            <option value="">All</option>
                            <option value="ADPOLY">ADPOLY</option>
                            <option value="FCHS">FCHS</option>
                        </select>
                    </div>
                    <div class="flex items-center gap-2">
                        <label class="text-sm font-medium text-gray-700">View:</label>
                        <div class="flex border border-gray-300 rounded-lg overflow-hidden">
                            <button @click="viewMode = 'list'"
                                :class="viewMode === 'list' ? 'bg-primary text-white' : 'bg-white text-gray-700 hover:bg-gray-50'"
                                class="px-4 py-2 text-sm font-medium transition-colors duration-200">
                                <font-awesome-icon icon="fa-solid fa-list" class="mr-2"></font-awesome-icon>
                                List
                            </button>
                            <button @click="viewMode = 'calendar'"
                                :class="viewMode === 'calendar' ? 'bg-primary text-white' : 'bg-white text-gray-700 hover:bg-gray-50'"
                                class="px-4 py-2 text-sm font-medium transition-colors duration-200">
                                <font-awesome-icon icon="fa-solid fa-calendar-days" class="mr-2"></font-awesome-icon>
                                Calendar
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Term Filter Tabs -->
        <div class="mb-6">
            <div class="flex flex-wrap gap-2">
                <button v-for="term in availableTerms" :key="term" @click="selectedTerm = term"
                    :class="selectedTerm === term ? 'bg-primary text-white' : 'bg-white text-primary border border-primary hover:bg-primary hover:text-white'"
                    class="px-4 py-2 rounded-full text-sm font-medium transition-all duration-200">
                    {{ term }}
                </button>
            </div>
        </div>

        <!-- Content Area -->
        <transition name="fade" mode="out-in">
            <div v-if="viewMode === 'list'" key="list" class="flex-1">
                <!-- Show content if there are filtered events -->
                <div v-if="hasFilteredEvents" class="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <!-- Fall Semester -->
                    <div v-if="showTerm('Fall Semester 2025-2026')"
                        class="bg-white rounded-xl shadow-lg overflow-hidden">
                        <div class="bg-gradient-to-r from-orange-500 to-orange-600 text-white p-4">
                            <h3 class="text-xl font-bold">Fall Semester 2025-2026</h3>
                            <p class="text-orange-100 text-sm">Abu Dhabi Polytechnic</p>
                        </div>
                        <div class="p-6">
                            <div class="space-y-3">
                                <academic-calendar-card v-for="event in getFilteredEvents('Fall Semester 2025-2026')"
                                    :key="event.id" :event="event"></academic-calendar-card>
                            </div>
                        </div>
                    </div>

                    <!-- Spring Semester -->
                    <div v-if="showTerm('Spring Semester 2025-2026')"
                        class="bg-white rounded-xl shadow-lg overflow-hidden">
                        <div class="bg-gradient-to-r from-green-500 to-green-600 text-white p-4">
                            <h3 class="text-xl font-bold">Spring Semester 2025-2026</h3>
                            <p class="text-green-100 text-sm">Abu Dhabi Polytechnic</p>
                        </div>
                        <div class="p-6">
                            <div class="space-y-3">
                                <academic-calendar-card v-for="event in getFilteredEvents('Spring Semester 2025-2026')"
                                    :key="event.id" :event="event"></academic-calendar-card>
                            </div>
                        </div>
                    </div>

                    <!-- Summer Term -->
                    <div v-if="showTerm('Summer Term 2025-2026')" class="bg-white rounded-xl shadow-lg overflow-hidden">
                        <div class="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-4">
                            <h3 class="text-xl font-bold">Summer Term 2025-2026</h3>
                            <p class="text-blue-100 text-sm">Abu Dhabi Polytechnic</p>
                        </div>
                        <div class="p-6">
                            <div class="space-y-3">
                                <academic-calendar-card v-for="event in getFilteredEvents('Summer Term 2025-2026')"
                                    :key="event.id" :event="event"></academic-calendar-card>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- No Data Message -->
                <div v-else class="flex flex-col items-center justify-center py-16 px-4">
                    <div class="text-center max-w-md">
                        <!-- Icon -->
                        <div class="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-gray-100 mb-6">
                            <font-awesome-icon icon="fa-solid fa-calendar-xmark"
                                class="h-10 w-10 text-gray-400"></font-awesome-icon>
                        </div>

                        <!-- Title -->
                        <h3 class="text-xl font-semibold text-gray-900 mb-2">
                            No Academic Calendar Data
                        </h3>

                        <!-- Description -->
                        <p class="text-gray-600 mb-6">
                            <span v-if="selectedInstitution && selectedInstitution !== ''">
                                No academic calendar data is available for <strong>{{
                                    getInstitutionName(selectedInstitution) }}</strong> at this time.
                            </span>
                            <span v-else>
                                No academic calendar data is available for the selected filters.
                            </span>
                        </p>

                        <!-- Action Buttons -->
                        <div class="flex flex-col sm:flex-row gap-3 justify-center">
                            <button @click="selectedInstitution = ''"
                                class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors duration-200">
                                <font-awesome-icon icon="fa-solid fa-eye" class="mr-2"></font-awesome-icon>
                                View All Entities
                            </button>
                            <button @click="selectedTerm = 'All'"
                                class="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors duration-200">
                                <font-awesome-icon icon="fa-solid fa-filter" class="mr-2"></font-awesome-icon>
                                Clear Filters
                            </button>
                        </div>

                        <!-- Additional Info -->
                        <div class="mt-6 p-4 bg-blue-50 rounded-lg">
                            <p class="text-sm text-blue-700">
                                <font-awesome-icon icon="fa-solid fa-info-circle" class="mr-2"></font-awesome-icon>
                                Academic calendar data will be updated as it becomes available from each institution.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <div v-else key="calendar" class="flex-1">
                <div v-if="hasFilteredEvents" class="bg-white rounded-xl shadow-lg overflow-hidden">
                    <div class="p-6">
                        <FullCalendar ref="fullCalendar" class="demo-app-calendar" :options="calendarOptions">
                            <template v-slot:eventContent="arg">
                                <div class="truncate text-xs font-medium">
                                    {{ arg.event.title }}
                                </div>
                            </template>
                        </FullCalendar>
                    </div>
                </div>

                <!-- No Data Message for Calendar View -->
                <div v-else class="flex flex-col items-center justify-center py-16 px-4">
                    <div class="text-center max-w-md">
                        <!-- Icon -->
                        <div class="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-gray-100 mb-6">
                            <font-awesome-icon icon="fa-solid fa-calendar-xmark"
                                class="h-10 w-10 text-gray-400"></font-awesome-icon>
                        </div>

                        <!-- Title -->
                        <h3 class="text-xl font-semibold text-gray-900 mb-2">
                            No Calendar Events
                        </h3>

                        <!-- Description -->
                        <p class="text-gray-600 mb-6">
                            <span v-if="selectedInstitution && selectedInstitution !== ''">
                                No academic calendar events are available for <strong>{{
                                    getInstitutionName(selectedInstitution)
                                }}</strong> at this time.
                            </span>
                            <span v-else>
                                No academic calendar events are available for the selected filters.
                            </span>
                        </p>

                        <!-- Action Buttons -->
                        <div class="flex flex-col sm:flex-row gap-3 justify-center">
                            <button @click="selectedInstitution = ''"
                                class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors duration-200">
                                <font-awesome-icon icon="fa-solid fa-eye" class="mr-2"></font-awesome-icon>
                                View All Entities
                            </button>
                            <button @click="selectedTerm = 'All'"
                                class="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors duration-200">
                                <font-awesome-icon icon="fa-solid fa-filter" class="mr-2"></font-awesome-icon>
                                Clear Filters
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import FullCalendar from '@fullcalendar/vue3'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import AcademicCalendarCard from '../../components/events/AcademicCalendarCard.vue'

export default {
    name: "academic-calendar",
    components: {
        FullCalendar,
        AcademicCalendarCard,
    },
    data() {
        return {
            selectedInstitution: '',
            viewMode: 'list',
            selectedTerm: 'All',
            calendarOptions: {
                plugins: [
                    dayGridPlugin,
                    timeGridPlugin,
                    interactionPlugin
                ],
                headerToolbar: {
                    left: 'prev,next today',
                    center: 'title',
                    right: 'dayGridMonth,timeGridWeek,timeGridDay'
                },
                initialView: 'dayGridMonth',
                editable: false,
                selectable: true,
                selectMirror: true,
                dayMaxEvents: true,
                weekends: true,
                events: [],
                eventDisplay: 'block',
                eventColor: '#394867',
                eventTextColor: '#ffffff',
                height: 'auto',
            },
            // Static ADPOLY Academic Calendar Data
            academicEvents: [
                // Fall Semester 2025-2026
                {
                    id: 1,
                    entity: 'ADPOLY',
                    term: 'Fall Semester 2025-2026',
                    title: 'Admission Period for Fall 2025',
                    description: 'Application period for new students',
                    dateRange: '02 Jun - 01 Aug 2025',
                    startDate: '2025-06-02',
                    endDate: '2025-08-01',
                    color: '#f97316'
                },
                {
                    id: 2,
                    entity: 'ADPOLY',
                    term: 'Fall Semester 2025-2026',
                    title: 'Faculty and Academic Staff Report',
                    description: 'Returning and new faculty report to work',
                    dateRange: '11 Aug 2025',
                    startDate: '2025-08-11',
                    endDate: '2025-08-11',
                    color: '#f97316'
                },
                {
                    id: 3,
                    entity: 'ADPOLY',
                    term: 'Fall Semester 2025-2026',
                    title: 'New Student Orientation',
                    description: 'Orientation, advising and registration for new students',
                    dateRange: '18-22 Aug 2025',
                    startDate: '2025-08-18',
                    endDate: '2025-08-22',
                    color: '#f97316'
                },
                {
                    id: 4,
                    entity: 'ADPOLY',
                    term: 'Fall Semester 2025-2026',
                    title: 'Makeup Exams',
                    description: 'Makeup examination period',
                    dateRange: '18-22 Aug 2025',
                    startDate: '2025-08-18',
                    endDate: '2025-08-22',
                    color: '#f97316'
                },
                {
                    id: 5,
                    entity: 'ADPOLY',
                    term: 'Fall Semester 2025-2026',
                    title: 'Fall Classes Begin',
                    description: 'First day of fall semester classes',
                    dateRange: '25 Aug 2025',
                    startDate: '2025-08-25',
                    endDate: '2025-08-25',
                    color: '#f97316'
                },
                {
                    id: 6,
                    entity: 'ADPOLY',
                    term: 'Fall Semester 2025-2026',
                    title: 'Add/Drop Period',
                    description: 'Course add/drop period for students',
                    dateRange: '25-29 Aug 2025',
                    startDate: '2025-08-25',
                    endDate: '2025-08-29',
                    color: '#f97316'
                },
                {
                    id: 7,
                    entity: 'ADPOLY',
                    term: 'Fall Semester 2025-2026',
                    title: 'Census Report',
                    description: 'Run census report (20th class day)',
                    dateRange: '19 Sep 2025',
                    startDate: '2025-09-19',
                    endDate: '2025-09-19',
                    color: '#f97316'
                },
                {
                    id: 8,
                    entity: 'ADPOLY',
                    term: 'Fall Semester 2025-2026',
                    title: 'Midterm Exams',
                    description: 'Midterm examination period',
                    dateRange: '13-24 Oct 2025',
                    startDate: '2025-10-13',
                    endDate: '2025-10-24',
                    color: '#f97316'
                },
                {
                    id: 9,
                    entity: 'ADPOLY',
                    term: 'Fall Semester 2025-2026',
                    title: 'Admission Period for Spring 2026',
                    description: 'Application period for Spring 2026 semester',
                    dateRange: '13 Oct - 12 Dec 2025',
                    startDate: '2025-10-13',
                    endDate: '2025-12-12',
                    color: '#f97316'
                },
                {
                    id: 10,
                    entity: 'ADPOLY',
                    term: 'Fall Semester 2025-2026',
                    title: 'Last Day for Course Withdrawal',
                    description: 'Last day for course withdrawal without penalty of WF grade',
                    dateRange: '28 Oct 2025',
                    startDate: '2025-10-28',
                    endDate: '2025-10-28',
                    color: '#f97316'
                },
                {
                    id: 11,
                    entity: 'ADPOLY',
                    term: 'Fall Semester 2025-2026',
                    title: 'Advising and Early Registration',
                    description: 'Advising and early registration for Spring semester',
                    dateRange: '03-07 Nov 2025',
                    startDate: '2025-11-03',
                    endDate: '2025-11-07',
                    color: '#f97316'
                },
                {
                    id: 12,
                    entity: 'ADPOLY',
                    term: 'Fall Semester 2025-2026',
                    title: 'Fall Final Exams',
                    description: 'Final examination period',
                    dateRange: '26 Nov - 05 Dec 2025',
                    startDate: '2025-11-26',
                    endDate: '2025-12-05',
                    color: '#f97316'
                },
                {
                    id: 13,
                    entity: 'ADPOLY',
                    term: 'Fall Semester 2025-2026',
                    title: 'Final Grades Due',
                    description: 'Final grades due to Registrar\'s Office',
                    dateRange: '09 Dec 2025',
                    startDate: '2025-12-09',
                    endDate: '2025-12-09',
                    color: '#f97316'
                },
                {
                    id: 14,
                    entity: 'ADPOLY',
                    term: 'Fall Semester 2025-2026',
                    title: 'Winter Break (Students)',
                    description: 'Winter break for students (4 weeks)',
                    dateRange: '08 Dec 2025 - 02 Jan 2026',
                    startDate: '2025-12-08',
                    endDate: '2026-01-02',
                    color: '#f97316'
                },
                {
                    id: 15,
                    entity: 'ADPOLY',
                    term: 'Fall Semester 2025-2026',
                    title: 'Winter Break (Faculty)',
                    description: 'Winter break for faculty and academic staff (3 weeks)',
                    dateRange: '08-26 Dec 2025',
                    startDate: '2025-12-08',
                    endDate: '2025-12-26',
                    color: '#f97316'
                },

                // Spring Semester 2025-2026
                {
                    id: 16,
                    entity: 'ADPOLY',
                    term: 'Spring Semester 2025-2026',
                    title: 'New Student Orientation',
                    description: 'Orientation, advising and registration for new students',
                    dateRange: '29 Dec 2025 - 02 Jan 2026',
                    startDate: '2025-12-29',
                    endDate: '2026-01-02',
                    color: '#10b981'
                },
                {
                    id: 17,
                    entity: 'ADPOLY',
                    term: 'Spring Semester 2025-2026',
                    title: 'Makeup Exams',
                    description: 'Makeup examination period',
                    dateRange: '29 Dec 2025 - 02 Jan 2026',
                    startDate: '2025-12-29',
                    endDate: '2026-01-02',
                    color: '#10b981'
                },
                {
                    id: 18,
                    entity: 'ADPOLY',
                    term: 'Spring Semester 2025-2026',
                    title: 'Spring Classes Begin',
                    description: 'First day of spring semester classes',
                    dateRange: '05 Jan 2026',
                    startDate: '2026-01-05',
                    endDate: '2026-01-05',
                    color: '#10b981'
                },
                {
                    id: 19,
                    entity: 'ADPOLY',
                    term: 'Spring Semester 2025-2026',
                    title: 'Add/Drop Period',
                    description: 'Course add/drop period for students',
                    dateRange: '05-09 Jan 2026',
                    startDate: '2026-01-05',
                    endDate: '2026-01-09',
                    color: '#10b981'
                },
                {
                    id: 20,
                    entity: 'ADPOLY',
                    term: 'Spring Semester 2025-2026',
                    title: 'Census Report',
                    description: 'Run census report (20th class day)',
                    dateRange: '30 Jan 2026',
                    startDate: '2026-01-30',
                    endDate: '2026-01-30',
                    color: '#10b981'
                },
                {
                    id: 21,
                    entity: 'ADPOLY',
                    term: 'Spring Semester 2025-2026',
                    title: 'Midterm Exams',
                    description: 'Midterm examination period',
                    dateRange: '23 Feb - 06 Mar 2026',
                    startDate: '2026-02-23',
                    endDate: '2026-03-06',
                    color: '#10b981'
                },
                {
                    id: 22,
                    entity: 'ADPOLY',
                    term: 'Spring Semester 2025-2026',
                    title: 'Last Day for Course Withdrawal',
                    description: 'Last day for course withdrawal without penalty of WF grade',
                    dateRange: '13 Mar 2026',
                    startDate: '2026-03-13',
                    endDate: '2026-03-13',
                    color: '#10b981'
                },
                {
                    id: 23,
                    entity: 'ADPOLY',
                    term: 'Spring Semester 2025-2026',
                    title: 'Spring Break (Students)',
                    description: 'Spring break for students (2 weeks)',
                    dateRange: '16 Mar - 27 Mar 2026',
                    startDate: '2026-03-16',
                    endDate: '2026-03-27',
                    color: '#10b981'
                },
                {
                    id: 24,
                    entity: 'ADPOLY',
                    term: 'Spring Semester 2025-2026',
                    title: 'Spring Break (Faculty)',
                    description: 'Spring break for faculty and academic staff (1 week)',
                    dateRange: '16-20 Mar 2026',
                    startDate: '2026-03-16',
                    endDate: '2026-03-20',
                    color: '#10b981'
                },
                {
                    id: 25,
                    entity: 'ADPOLY',
                    term: 'Spring Semester 2025-2026',
                    title: 'Staff Professional Development',
                    description: 'Staff Professional Development (PD) Week',
                    dateRange: '23-27 Mar 2026',
                    startDate: '2026-03-23',
                    endDate: '2026-03-27',
                    color: '#10b981'
                },
                {
                    id: 26,
                    entity: 'ADPOLY',
                    term: 'Spring Semester 2025-2026',
                    title: 'Advising and Early Registration',
                    description: 'Advising and early registration for Summer',
                    dateRange: '06-10 Apr 2026',
                    startDate: '2026-04-06',
                    endDate: '2026-04-10',
                    color: '#10b981'
                },
                {
                    id: 27,
                    entity: 'ADPOLY',
                    term: 'Spring Semester 2025-2026',
                    title: 'Spring Final Exams',
                    description: 'Final examination period',
                    dateRange: '04-15 May 2026',
                    startDate: '2026-05-04',
                    endDate: '2026-05-15',
                    color: '#10b981'
                },
                {
                    id: 28,
                    entity: 'ADPOLY',
                    term: 'Spring Semester 2025-2026',
                    title: 'Final Grades Due',
                    description: 'Final grades due to Registrar\'s Office',
                    dateRange: '17 May 2026',
                    startDate: '2026-05-17',
                    endDate: '2026-05-17',
                    color: '#10b981'
                },

                // Summer Term 2025-2026
                {
                    id: 29,
                    entity: 'ADPOLY',
                    term: 'Summer Term 2025-2026',
                    title: 'Makeup Exams',
                    description: 'Makeup examination period',
                    dateRange: '18-19 May 2026',
                    startDate: '2026-05-18',
                    endDate: '2026-05-19',
                    color: '#3b82f6'
                },
                {
                    id: 30,
                    entity: 'ADPOLY',
                    term: 'Summer Term 2025-2026',
                    title: 'Summer Classes Begin',
                    description: 'First day of summer term classes',
                    dateRange: '20 May 2026',
                    startDate: '2026-05-20',
                    endDate: '2026-05-20',
                    color: '#3b82f6'
                },
                {
                    id: 31,
                    entity: 'ADPOLY',
                    term: 'Summer Term 2025-2026',
                    title: 'Add/Drop Period',
                    description: 'Course add/drop period for students',
                    dateRange: '20-22 May 2026',
                    startDate: '2026-05-20',
                    endDate: '2026-05-22',
                    color: '#3b82f6'
                },
                {
                    id: 32,
                    entity: 'ADPOLY',
                    term: 'Summer Term 2025-2026',
                    title: 'Midterm Exams',
                    description: 'Midterm examination period',
                    dateRange: '15-19 Jun 2026',
                    startDate: '2026-06-15',
                    endDate: '2026-06-19',
                    color: '#3b82f6'
                },
                {
                    id: 33,
                    entity: 'ADPOLY',
                    term: 'Summer Term 2025-2026',
                    title: 'Last Day for Course Withdrawal',
                    description: 'Last day for course withdrawal without penalty of WF grade',
                    dateRange: '24 Jun 2026',
                    startDate: '2026-06-24',
                    endDate: '2026-06-24',
                    color: '#3b82f6'
                },
                {
                    id: 34,
                    entity: 'ADPOLY',
                    term: 'Summer Term 2025-2026',
                    title: 'Summer Final Exams',
                    description: 'Final examination period',
                    dateRange: '29 Jun - 02 Jul 2026',
                    startDate: '2026-06-29',
                    endDate: '2026-07-02',
                    color: '#3b82f6'
                },
                {
                    id: 35,
                    entity: 'ADPOLY',
                    term: 'Summer Term 2025-2026',
                    title: 'Final Grades Due',
                    description: 'Final grades due to Registrar\'s Office',
                    dateRange: '04 Jul 2026',
                    startDate: '2026-07-04',
                    endDate: '2026-07-04',
                    color: '#3b82f6'
                },
                {
                    id: 36,
                    entity: 'ADPOLY',
                    term: 'Summer Term 2025-2026',
                    title: 'Summer Break',
                    description: 'Summer break begins',
                    dateRange: '06 Jul 2026',
                    startDate: '2026-07-06',
                    endDate: '2026-07-06',
                    color: '#3b82f6'
                },
                {
                    id: 37,
                    entity: 'ADPOLY',
                    term: 'Summer Term 2025-2026',
                    title: 'Faculty Report for Fall 2026-2027',
                    description: 'Faculty and academic staff report to work for Fall academic year 2026-2027',
                    dateRange: '17 Aug 2026',
                    startDate: '2026-08-17',
                    endDate: '2026-08-17',
                    color: '#3b82f6'
                }
            ]
        }
    },
    computed: {
        availableTerms() {
            const terms = ['All'];
            const uniqueTerms = [...new Set(this.academicEvents.map(event => event.term))];
            return [...terms, ...uniqueTerms];
        },
        filteredEvents() {
            let events = this.academicEvents;

            if (this.selectedInstitution !== '' && this.selectedInstitution !== '') {
                events = events.filter(event => event.entity === this.selectedInstitution);
            }

            if (this.selectedTerm !== 'All') {
                events = events.filter(event => event.term === this.selectedTerm);
            }

            return events;
        },
        hasFilteredEvents() {
            return this.filteredEvents.length > 0;
        }
    },
    watch: {
        filteredEvents: {
            handler() {
                this.updateCalendarEvents();
            },
            immediate: true
        },
        selectedTerm: {
            handler(newTerm) {
                if (newTerm !== 'All' && this.viewMode === 'calendar') {
                    this.navigateToTerm(newTerm);
                }
            }
        },
        viewMode: {
            handler(newMode) {
                if (newMode === 'calendar' && this.selectedTerm !== 'All') {
                    // Wait for calendar to be fully rendered
                    this.$nextTick(() => {
                        this.waitForCalendarAndNavigate();
                    });
                }
            }
        }
    },
    methods: {
        showTerm(term) {
            if (this.selectedTerm === 'All') {
                return this.filteredEvents.some(event => event.term === term);
            }
            return this.selectedTerm === term;
        },
        getFilteredEvents(term) {
            return this.filteredEvents.filter(event => event.term === term);
        },
        updateCalendarEvents() {
            const calendarEvents = this.filteredEvents.map(event => ({
                id: event.id,
                title: event.title,
                start: event.startDate,
                end: event.endDate,
                backgroundColor: event.color,
                borderColor: event.color,
                textColor: '#ffffff',
                extendedProps: {
                    description: event.description,
                    entity: event.entity,
                    term: event.term
                }
            }));

            this.calendarOptions.events = calendarEvents;
        },
        getInstitutionName(entity) {
            const institutionNames = {
                'ADPOLY': 'Abu Dhabi Polytechnic',
                'FCHS': 'Fatima College of Health Sciences',
                'IAT': 'Institute of Applied Technology',
                'ADVETI': 'Abu Dhabi Vocational Education and Training Institute',
                'ATS': 'Applied Technology Schools'
            };
            return institutionNames[entity] || entity;
        },
        getTermDateRange(term) {
            const termRanges = {
                'Fall Semester 2025-2026': {
                    start: '2025-06-01',
                    end: '2025-12-31'
                },
                'Spring Semester 2025-2026': {
                    start: '2025-12-01',
                    end: '2026-05-31'
                },
                'Summer Term 2025-2026': {
                    start: '2026-05-01',
                    end: '2026-08-31'
                }
            };
            return termRanges[term] || null;
        },
        waitForCalendarAndNavigate() {
            let attempts = 0;
            const maxAttempts = 10;

            const checkCalendar = () => {
                attempts++;
                if (this.$refs.fullCalendar && this.$refs.fullCalendar.getApi) {
                    try {
                        const api = this.$refs.fullCalendar.getApi();
                        if (api && api.getCurrentData) {
                            // Calendar is ready, navigate to term
                            this.navigateToTerm(this.selectedTerm);
                            return;
                        }
                    } catch (error) {
                        // Calendar not ready yet
                    }
                }

                if (attempts < maxAttempts) {
                    setTimeout(checkCalendar, 100);
                }
            };

            checkCalendar();
        },
        navigateToTerm(term) {
            const dateRange = this.getTermDateRange(term);
            if (dateRange && this.$refs.fullCalendar) {
                try {
                    const calendarApi = this.$refs.fullCalendar.getApi();

                    // Navigate to the earliest event of the term for better user experience
                    const termEvents = this.academicEvents.filter(event => event.term === term);
                    if (termEvents.length > 0) {
                        // Find the earliest event start date
                        const earliestEvent = termEvents.reduce((earliest, current) => {
                            return new Date(current.startDate) < new Date(earliest.startDate) ? current : earliest;
                        });

                        // Navigate to the month containing the earliest event
                        const earliestMonth = earliestEvent.startDate.substring(0, 7); // YYYY-MM
                        calendarApi.gotoDate(earliestMonth + '-01');
                    } else {
                        // Fallback to start date if no events found
                        calendarApi.gotoDate(dateRange.start);
                    }

                    // Ensure the calendar is properly rendered
                    setTimeout(() => {
                        calendarApi.render();
                    }, 100);
                } catch (error) {
                    console.log('Error navigating to term:', error);
                    // Fallback: try again after a longer delay
                    setTimeout(() => {
                        this.navigateToTerm(term);
                    }, 1000);
                }
            }
        }
    }
}
</script>

<style lang="scss" scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

:deep(.fc) {
    .fc-event {
        border-radius: 4px;
        font-size: 0.75rem;
        padding: 2px 4px;
    }

    .fc-daygrid-event {
        margin: 1px 0;
    }

    .fc-event-title {
        font-weight: 500;
    }

    .fc-toolbar-title {
        font-size: 1.5rem;
        font-weight: 600;
        color: #374151;
    }

    .fc-button {
        background-color: #394867;
        border-color: #394867;
        font-weight: 500;

        &:hover {
            background-color: #2d3748;
            border-color: #2d3748;
        }

        &.fc-button-active {
            background-color: #1d337c;
            border-color: #1d337c;
        }
    }

    .fc-daygrid-day-number {
        color: #374151;
        font-weight: 500;
    }

    .fc-col-header-cell {
        background-color: #f9fafb;
        color: #374151;
        font-weight: 600;
    }
}
</style>
